import {
  Button,
  ButtonsWrapper,
  Form,
  PhoneNumberInput,
  PLATFORM_THEME_COLORS,
  TextInput,
  TimezoneSelect,
  Typography,
  TYPOGRAPHY_SIZE,
  AddressInput,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';
import { useTranslation } from 'react-i18next';

import { useUpdateCurrentCompany } from 'src/hooks/companies/update-current-company.hook';
import { ICompanyData } from 'src/pages/settings/components/company/company.interfaces';

import { FormContentWrapper, FormItemWrapper, FormWrapper } from './company-edit.styles';

const CompanyEdit = ({
  onCancelEdit,
  companyData,
}: {
  onCancelEdit: () => void;
  companyData: ICompanyData;
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'settings', 'auth']);
  const updateCurrentCompany = useUpdateCurrentCompany();

  const onFinish = (values: any) => {
    const { ownerCompany, ...rest } = values;

    updateCurrentCompany({
      variables: {
        payload: {
          ...rest,
          ...ownerCompany,
        },
      },
    }).then(() => onCancelEdit());
  };

  return (
    <FormWrapper>
      <Form
        layout="vertical"
        onFinish={onFinish}
        form={form}
        initialValues={{
          ...companyData,
          ownerCompany: Object.values(companyData.ownerCompany).every(item => item)
            ? companyData.ownerCompany
            : {
                city: companyData.city,
                street: companyData.street,
                zipCode: companyData.zipCode,
                country: companyData.country,
              },
          timezone: companyData.timezone === 2 ? undefined : companyData.timezone,
        }}
      >
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('settings:company.labels.companyProfile')}
        </Typography>
        <FormContentWrapper>
          <FormItemWrapper>
            <Form.Item
              name="name"
              label={t('settings:company.labels.companyName')}
              rules={[
                { required: true, message: t('settings:company.exceptions.requiredCompanyName') },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput maxLength={100} />
            </Form.Item>
            <Form.Item
              name="phoneNumber"
              label={t('settings:company.labels.companyPhoneNumber')}
              rules={[
                { required: true, message: t('common:validation.error.phoneNumberRequired') },
              ]}
            >
              <PhoneNumberInput placeholder="+49 176 384 15749" />
            </Form.Item>
          </FormItemWrapper>
          <FormItemWrapper>
            <Form.Item
              name="email"
              label={t('settings:company.labels.companyEmailAddress')}
              rules={[{ type: 'email', message: t('auth:exceptions.notValidEmail') }]}
            >
              <TextInput maxLength={50} placeholder="solomonolov@gmail.com" />
            </Form.Item>
            <Form.Item name="timezone" label={t('settings:company.labels.timezone')}>
              <TimezoneSelect placeholder="GMT +02:00" />
            </Form.Item>
          </FormItemWrapper>
        </FormContentWrapper>
        <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
          {t('common:labels.businessAddress')}
        </Typography>
        <FormContentWrapper>
          <AddressInput t={t} twoColumn parentField="ownerCompany" />
        </FormContentWrapper>
        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.change')}
          </Button>
          <Button onClick={onCancelEdit}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </FormWrapper>
  );
};

export default CompanyEdit;
